import React, { useState } from 'react';
import { Input, Button, DatePicker, Select, InputNumber } from 'antd';
import { CalendarOutlined, SearchOutlined, ClearOutlined, EnvironmentOutlined, DollarOutlined } from '@ant-design/icons';
import { GenericSearchProps, SearchFilters } from '../../types/TableTypes';
import './Search.css';

const GenericSearch: React.FC<GenericSearchProps> = ({
  onSearch,
  onReset,
  loading = false,
  showDateFilters = true,
  keywordPlaceholder = 'キーワードで検索...',
  stationPlaceholder,
  districtOptions = [],
  districtPlaceholder = '区を選択',
  showPriceFilter = false,
}) => {
  const [searchValues, setSearchValues] = useState<SearchFilters>({});

  const handleFieldChange = (fieldKey: string, value: any) => {
    setSearchValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSearch = () => {
    const filters: SearchFilters = {};
    Object.keys(searchValues).forEach(key => {
      const value = searchValues[key];
      if (value !== undefined && value !== null && value !== '' &&
        !(Array.isArray(value) && value.length === 0)) {
        filters[key] = value;
      }
    });

    onSearch(filters);
  };

  const handleReset = () => {
    setSearchValues({});
    onReset();
  };

  const handleEnterPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className='search-container'>
      <div className='search-row'>
        <div className='search-item keyword'>
          <Input
            placeholder={keywordPlaceholder}
            value={searchValues.keyword || ""}
            onChange={(e) => handleFieldChange("keyword", e.target.value)}
            onPressEnter={handleEnterPress}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
            style={{ minWidth: 180, width: '100%' }}
          />
        </div>
        {stationPlaceholder && (
        <div className='search-item keyword'>
          <Input
            placeholder={stationPlaceholder}
            value={searchValues.station || ""}
            onChange={(e) => handleFieldChange("station", e.target.value)}
            onPressEnter={handleEnterPress}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
            style={{ minWidth: 180, width: '100%' }}
          />
        </div>
        )}
        {districtOptions.length > 0 && (
        <div className='search-item district'>
          <Select
            mode="multiple"
            placeholder={districtPlaceholder}
            value={searchValues.district || []}
            onChange={(value) => handleFieldChange("district", value)}
            options={districtOptions}
            allowClear
            size="large"
            maxTagCount="responsive"
            suffixIcon={<EnvironmentOutlined />}
            style={{ minWidth: 200, width: '100%' }}
          />
        </div>
        )}
        {showPriceFilter && (
        <div className='search-item price'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <DollarOutlined style={{ color: '#8c8c8c', fontSize: 16, flexShrink: 0 }} />
            <InputNumber
              placeholder="価格下限"
              min={0}
              value={searchValues.priceMin ?? null}
              onChange={(value) => handleFieldChange("priceMin", value)}
              style={{ width: 120 }}
              size="large"
            />
            <span style={{ color: '#8c8c8c', flexShrink: 0 }}>〜</span>
            <InputNumber
              placeholder="価格上限"
              min={0}
              value={searchValues.priceMax ?? null}
              onChange={(value) => handleFieldChange("priceMax", value)}
              style={{ width: 120 }}
              size="large"
            />
            <span style={{ color: '#8c8c8c', flexShrink: 0 }}>万円</span>
          </div>
        </div>
        )}
        {showDateFilters && (
        <div className='search-item date-column'>
          {/* 上段: 売出物件 */}
          <div className='date-row'>
            <div className='search-item date with-label'>
              <div className='search-date-label'>売出物件</div>
              <DatePicker
                placeholder='年/月/日 時:分'
                value={searchValues.saleDateStart || null}
                onChange={(date) => handleFieldChange("saleDateStart", date)}
                format="YYYY/MM/DD HH:mm"
                showTime={{ format: 'HH:mm' }}
                size="large"
                style={{ minWidth: 170, maxWidth: 170, width: '100%' }}
                suffixIcon={<CalendarOutlined />}
              />
            </div>
            <span className='search-date-separator'>〜</span>
            <div className='search-item date'>
              <DatePicker
                placeholder='年/月/日 時:分'
                value={searchValues.saleDateEnd || null}
                onChange={(date) => handleFieldChange("saleDateEnd", date)}
                format="YYYY/MM/DD HH:mm"
                showTime={{ format: 'HH:mm' }}
                size="large"
                style={{ minWidth: 170, maxWidth: 170, width: '100%' }}
                suffixIcon={<CalendarOutlined />}
              />
            </div>
          </div>

          {/* 下段: 成約物件 */}
          <div className='date-row'>
            <div className='search-item date with-label'>
              <div className='search-date-label'>成約物件</div>
              <DatePicker
                placeholder='年/月/日 時:分'
                value={searchValues.contractedDateStart || null}
                onChange={(date) => handleFieldChange("contractedDateStart", date)}
                format="YYYY/MM/DD HH:mm"
                showTime={{ format: 'HH:mm' }}
                size="large"
                style={{ minWidth: 170, maxWidth: 170, width: '100%' }}
                suffixIcon={<CalendarOutlined />}
              />
            </div>
            <span className='search-date-separator'>〜</span>
            <div className='search-item date'>
              <DatePicker
                placeholder='年/月/日 時:分'
                value={searchValues.contractedDateEnd || null}
                onChange={(date) => handleFieldChange("contractedDateEnd", date)}
                format="YYYY/MM/DD HH:mm"
                showTime={{ format: 'HH:mm' }}
                size="large"
                style={{ minWidth: 170, maxWidth: 170, width: '100%' }}
                suffixIcon={<CalendarOutlined />}
              />
            </div>
          </div>
        </div>
        )}
        <div className='search-item actions'>
          <Button
            type='primary'
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            size="large"
            style={{ minWidth: 90, marginRight: 8 }}
          >
            検索
          </Button>
          <Button 
            icon={<ClearOutlined />} 
            onClick={handleReset}
            size="large"
            style={{ minWidth: 90 }}
          >
            フィルターをクリア
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GenericSearch;